import React, { useState, useEffect } from 'react';
import { authFetch } from '../lib/authFetch';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Plus,
  Clock,
  MapPin,
  User,
  Repeat
} from 'lucide-react';

const CalendarView = () => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('week'); // 'day', 'week', 'month'
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [recurringForm, setRecurringForm] = useState({
    customer_name: '',
    patient_name: '',
    phone: '',
    pickup_address: '',
    destination: '',
    time: '08:00',
    mobility_type: 'Wheelchair',
    pattern: 'MWF', // MWF, TTS, Daily, Custom
    start_date: '',
    end_date: '',
    notes: 'Recurring Dialysis Transport'
  });

  const fetchBookings = React.useCallback(async () => {
    try {
      const response = await authFetch(`/api/bookings`);
      const data = await response.json();
      setBookings(data);
    } catch (err) {
      console.error('[CalendarView] Failed to fetch bookings:', err);
      // Show error message to user
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBookings();
  }, [navigate, currentDate, fetchBookings]);

  const handleRecurringSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await authFetch(`/api/bookings/recurring`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(recurringForm)
      });

      if (response.ok) {
        setShowRecurringModal(false);
        fetchBookings();
        alert('Recurring rides scheduled successfully!');
      } else {
        alert('Failed to schedule recurring rides');
      }
    } catch (err) {
      console.error('Failed to create recurring rides:', err);
      alert('Error scheduling rides');
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const getWeekDays = (date) => {
    const days = [];
    const current = new Date(date);
    current.setDate(current.getDate() - current.getDay()); // Start from Sunday
    
    for (let i = 0; i < 7; i++) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return days;
  };

  const getBookingsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return bookings.filter(booking => booking.date === dateStr);
  };

  const navigateDate = (direction) => {
    const newDate = new Date(currentDate);
    if (view === 'day') {
      newDate.setDate(newDate.getDate() + direction);
    } else if (view === 'week') {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else {
      newDate.setMonth(newDate.getMonth() + direction);
    }
    setCurrentDate(newDate);
  };

  const getStatusColor = (status) => {
    const colors = {
      'Pending': 'bg-yellow-100 border-yellow-300 text-yellow-800',
      'Confirmed': 'bg-blue-100 border-blue-300 text-blue-800',
      'Assigned': 'bg-purple-100 border-purple-300 text-purple-800',
      'DriverEnRoute': 'bg-green-100 border-green-300 text-green-800',
      'Completed': 'bg-gray-100 border-gray-300 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 border-gray-300 text-gray-800';
  };

  const renderDayView = () => {
    const dayBookings = getBookingsForDate(currentDate);
    const hours = Array.from({ length: 24 }, (_, i) => i);

    return (
      <div className="bg-white rounded-lg border-2 overflow-hidden">
        <div className="sticky top-0 bg-gray-50 border-b-2 p-4">
          <h3 className="text-lg font-semibold text-[#004C97]">
            {currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
          </h3>
          <p className="text-sm text-gray-600">{dayBookings.length} rides scheduled</p>
        </div>
        
        <div className="p-4 space-y-2">
          {dayBookings.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <CalendarIcon className="w-12 h-12 mx-auto mb-2 text-gray-400" />
              <p>No rides scheduled for this day</p>
            </div>
          ) : (
            dayBookings.map((booking) => (
              <Card key={booking.id} className={`border-2 ${getStatusColor(booking.status)} cursor-pointer hover:shadow-md transition-all`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <Clock className="w-4 h-4 text-[#F9A825]" />
                        <span className="font-semibold text-[#004C97]">{booking.time}</span>
                        <Badge variant="secondary" className="text-xs">{booking.status}</Badge>
                      </div>
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center space-x-2">
                          <User className="w-3 h-3 text-gray-400" />
                          <span className="font-medium">{booking.customer_name}</span>
                          <span className="text-gray-500">({booking.patient_name})</span>
                        </div>
                        <div className="flex items-start space-x-2 text-gray-600">
                          <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="line-clamp-1">{booking.pickup_address}</p>
                            <p className="line-clamp-1">→ {booking.destination}</p>
                          </div>
                        </div>
                        <div className="text-xs text-gray-500">
                          {booking.mobility_type} • {booking.assigned_driver_name || 'Unassigned'}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    const weekDays = getWeekDays(currentDate);

    return (
      <div className="bg-white rounded-lg border-2 overflow-hidden">
        <div className="grid grid-cols-7 border-b-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <div key={day} className="p-3 text-center border-r last:border-r-0 bg-gray-50">
              <p className="text-sm font-semibold text-gray-700">{day}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7">
          {weekDays.map((day) => {
            const dayBookings = getBookingsForDate(day);
            const isToday = day.toDateString() === new Date().toDateString();
            const dayKey = day.toISOString().split('T')[0];

            return (
              <div
                key={dayKey}
                className={`min-h-[300px] p-2 border-r border-b last:border-r-0 ${isToday ? 'bg-blue-50' : ''}`}
              >
                <div className={`text-center mb-2 ${isToday ? 'bg-[#F9A825] text-white rounded-full w-8 h-8 flex items-center justify-center mx-auto font-bold' : 'text-gray-700'}`}>
                  {day.getDate()}
                </div>

                <div className="space-y-1">
                  {dayBookings.map((booking) => (
                    <div
                      key={booking.id}
                      className={`text-xs p-2 rounded border ${getStatusColor(booking.status)} cursor-pointer hover:shadow-sm`}
                    >
                      <p className="font-semibold truncate">{booking.time}</p>
                      <p className="truncate">{booking.customer_name}</p>
                      <p className="text-xs truncate">{booking.mobility_type}</p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderMonthView = () => {
    const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentDate);
    const days = [];

    // Add empty cells for days before the first of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add cells for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return (
      <div className="bg-white rounded-lg border-2 overflow-hidden">
        <div className="grid grid-cols-7 border-b-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <div key={day} className="p-3 text-center border-r last:border-r-0 bg-gray-50">
              <p className="text-sm font-semibold text-gray-700">{day}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7">
          {days.map((day, idx) => {
            if (!day) {
              return <div key={`empty-${idx}`} className="min-h-[120px] p-2 bg-gray-50 border-r border-b"></div>;
            }

            const dayBookings = getBookingsForDate(day);
            const isToday = day.toDateString() === new Date().toDateString();
            const dayKey = day.toISOString().split('T')[0];

            return (
              <div
                key={dayKey}
                className={`min-h-[120px] p-2 border-r border-b ${isToday ? 'bg-blue-50' : ''}`}
              >
                <div className={`text-sm mb-1 ${isToday ? 'bg-[#F9A825] text-white rounded-full w-6 h-6 flex items-center justify-center font-bold' : 'text-gray-700'}`}>
                  {day.getDate()}
                </div>

                <div className="space-y-1">
                  {dayBookings.slice(0, 3).map((booking) => (
                    <div
                      key={booking.id}
                      className={`text-xs p-1 rounded ${getStatusColor(booking.status)} cursor-pointer`}
                    >
                      <p className="font-semibold truncate">{booking.time}</p>
                      <p className="truncate">{booking.customer_name}</p>
                    </div>
                  ))}
                  {dayBookings.length > 3 && (
                    <p className="text-xs text-gray-500 pl-1">+{dayBookings.length - 3} more</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F9A825] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading calendar...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#004C97] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                onClick={() => navigate('/dispatch/dashboard')}
                variant="ghost"
                className="text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <h1 className="text-2xl font-bold text-white">Calendar View</h1>
            </div>
            <Button
              onClick={() => setShowRecurringModal(true)}
              className="bg-[#F9A825] hover:bg-[#E08E1E] text-white"
            >
              <Repeat className="w-4 h-4 mr-2" />
              Schedule Recurring
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Controls */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <Button
              onClick={() => navigateDate(-1)}
              variant="outline"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <Button
              onClick={() => setCurrentDate(new Date())}
              variant="outline"
            >
              Today
            </Button>
            <Button
              onClick={() => navigateDate(1)}
              variant="outline"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
            <h2 className="text-xl font-semibold text-[#004C97]">
              {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </h2>
          </div>

          <div className="flex space-x-2">
            <Button
              onClick={() => setView('day')}
              variant={view === 'day' ? 'default' : 'outline'}
              className={view === 'day' ? 'bg-[#004C97] text-white' : ''}
            >
              Day
            </Button>
            <Button
              onClick={() => setView('week')}
              variant={view === 'week' ? 'default' : 'outline'}
              className={view === 'week' ? 'bg-[#004C97] text-white' : ''}
            >
              Week
            </Button>
            <Button
              onClick={() => setView('month')}
              variant={view === 'month' ? 'default' : 'outline'}
              className={view === 'month' ? 'bg-[#004C97] text-white' : ''}
            >
              Month
            </Button>
          </div>
        </div>

        {/* Calendar Display */}
        {view === 'day' && renderDayView()}
        {view === 'week' && renderWeekView()}
        {view === 'month' && renderMonthView()}
      </main>

      {/* Recurring Modal */}
      {showRecurringModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <Card className="w-full max-w-2xl border-2 border-[#F9A825] my-8">
            <CardHeader>
              <CardTitle className="text-[#004C97] flex items-center space-x-2">
                <Repeat className="w-5 h-5" />
                <span>Schedule Recurring Rides (Dialysis)</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleRecurringSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Customer Name *</label>
                    <input
                      type="text"
                      required
                      value={recurringForm.customer_name}
                      onChange={(e) => setRecurringForm({ ...recurringForm, customer_name: e.target.value })}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F9A825]"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Patient Name *</label>
                    <input
                      type="text"
                      required
                      value={recurringForm.patient_name}
                      onChange={(e) => setRecurringForm({ ...recurringForm, patient_name: e.target.value })}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F9A825]"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Phone *</label>
                    <input
                      type="tel"
                      required
                      value={recurringForm.phone}
                      onChange={(e) => setRecurringForm({ ...recurringForm, phone: e.target.value })}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F9A825]"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Time *</label>
                    <input
                      type="time"
                      required
                      value={recurringForm.time}
                      onChange={(e) => setRecurringForm({ ...recurringForm, time: e.target.value })}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F9A825]"
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Pickup Address *</label>
                    <input
                      type="text"
                      required
                      value={recurringForm.pickup_address}
                      onChange={(e) => setRecurringForm({ ...recurringForm, pickup_address: e.target.value })}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F9A825]"
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Destination (Dialysis Center) *</label>
                    <input
                      type="text"
                      required
                      value={recurringForm.destination}
                      onChange={(e) => setRecurringForm({ ...recurringForm, destination: e.target.value })}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F9A825]"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Recurrence Pattern *</label>
                    <select
                      value={recurringForm.pattern}
                      onChange={(e) => setRecurringForm({ ...recurringForm, pattern: e.target.value })}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F9A825]"
                    >
                      <option value="MWF">Monday, Wednesday, Friday</option>
                      <option value="TTS">Tuesday, Thursday, Saturday</option>
                      <option value="Daily">Every Day</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Mobility Type *</label>
                    <select
                      value={recurringForm.mobility_type}
                      onChange={(e) => setRecurringForm({ ...recurringForm, mobility_type: e.target.value })}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F9A825]"
                    >
                      <option>Wheelchair</option>
                      <option>Ambulatory</option>
                      <option>Stretcher</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Start Date *</label>
                    <input
                      type="date"
                      required
                      value={recurringForm.start_date}
                      onChange={(e) => setRecurringForm({ ...recurringForm, start_date: e.target.value })}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F9A825]"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">End Date *</label>
                    <input
                      type="date"
                      required
                      value={recurringForm.end_date}
                      onChange={(e) => setRecurringForm({ ...recurringForm, end_date: e.target.value })}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F9A825]"
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Notes</label>
                    <textarea
                      value={recurringForm.notes}
                      onChange={(e) => setRecurringForm({ ...recurringForm, notes: e.target.value })}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F9A825]"
                      rows="2"
                    />
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    This will create individual bookings for each day in the selected pattern from {recurringForm.start_date || 'start date'} to {recurringForm.end_date || 'end date'}.
                  </p>
                </div>

                <div className="flex space-x-3">
                  <Button
                    type="submit"
                    className="flex-1 bg-[#F9A825] hover:bg-[#E08E1E] text-white"
                  >
                    Create Recurring Rides
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setShowRecurringModal(false)}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default CalendarView;
